/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable unused-imports/no-unused-vars */
import React, { useRef, useState, lazy, Suspense, useCallback, useMemo } from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import {
	Box,
	Paper,
	MenuItem,
	FormControl,
	IconButton,
	Fade,
	FormControlLabel,
	Skeleton
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SplitIcon from '@mui/icons-material/CallSplit';
import { SectionData } from '../types/editor';
import { Edit, Save, Trash } from '@/components/icons';
import Select from '@/components/select/select';
import Typography from '@/components/typography/typography';
import EditSectionModal from '../edit-section-modal/edit-section-modal';
import Checkbox from '@/components/checkbox/checkbox';
import Chip from '@/components/chip/chip';
import Close from '@mui/icons-material/Close';
import { useCKEditorStyleExtractor } from '../utils/style-extractor';
import { useDebouncedUpdate } from '../hooks/useDebouncedUpdate';

const CustomEditor = lazy(() => import('@/components/editor/custom-editor'));

interface EditorSectionProps {
	section: SectionData;
	isSelected: boolean;
	isDragging: boolean;
	isOver: boolean;
	onSelect: () => void;
	onUpdate: (content: string, options?: any) => void;
	onSplit: (content1: string, content2: string) => void;
	onDelete: () => void;
	placeholders: Array<{ value: string; label: string }>;
	setIsSectionEditing: (value: boolean) => void;
	isPreview?: boolean;
}

const editableSections = ['text', 'text-block', 'heading', 'heading-1', 'heading-2', 'heading-3', 'heading-4'];

const EditorSection: React.FC<EditorSectionProps> = ({
	section,
	isSelected,
	isDragging,
	isOver,
	onSelect,
	onUpdate,
	onSplit,
	onDelete,
	placeholders,
	setIsSectionEditing,
	isPreview = false
}) => {
	const { extractStyles, getSingleStyles } = useCKEditorStyleExtractor();

	const {
		attributes,
		listeners,
		setNodeRef: setDraggableRef,
		transform
	} = useDraggable({ id: section.id });

	const { setNodeRef: setDroppableRef } = useDroppable({ id: section.id });
	const [isEditing, setIsEditing] = useState(false);
	const [isHovered, setIsHovered] = useState(false);
	const [isOpenEditSectionModal, setIsOpenEditSectionModal] = useState(false);
	const [isEditedFromSource, setIsEditedFromSource] = useState(Boolean(section?.is_edited_from_source));
	const [localContent, setLocalContent] = useState(section.content);
	const [hasUserEdited, setHasUserEdited] = useState(false); // Track if user actually made changes
	const editorRef = useRef<any>(null);
	const mouseHoverTimeoutRef = useRef<number | null>(null);
	const cursorPositionRef = useRef<{ from: number; to: number } | null>(null);
	const isRestoringCursorRef = useRef(false);

	// Ref-based content tracking for contentEditable optimization
	// This ref tracks content changes without triggering re-renders during editing,
	// improving performance and preventing cursor position loss
	const contentRef = useRef<string>(section.content);
	const originalContentRef = useRef<string>(section.content); // Store original content

	// Create debounced version of onUpdate with 300ms delay
	const debouncedUpdate = useDebouncedUpdate(onUpdate, 300);

	// Memoize style calculation
	const style = useMemo(() => {
		return transform
			? {
				transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
				transition: 'none'
			}
			: { transition: 'all 0.2s ease' };
	}, [transform]);

	// Memoize sx prop for Paper to prevent re-creation
	const paperSx = useMemo(() => ({
		position: 'relative',
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
		overflowX: 'auto',
		border: isPreview ? 'none' : '1px dashed',
		borderColor: isPreview ? 'transparent' : (isSelected ? 'primary.main' : isOver ? 'primary.main' : 'grey.400'),
		borderRadius: 1,
		bgcolor: 'white',
		opacity: isDragging ? 0.7 : 1,
		boxShadow: 'none',
		transition: 'all 0.2s ease',
		'&:hover': {
			boxShadow: 'none'
		},
		'& table:not(.pbd-items)': {
			minWidth: 'max-content'
		},
		...(!isPreview && isOver && { borderColor: 'primary.main', boxShadow: 'none' })
	}), [isSelected, isOver, isDragging, isPreview]);

	// Memoize merged ref to prevent re-creation
	const mergedRef = useCallback((node: HTMLElement | null) => {
		setDraggableRef(node);
		setDroppableRef(node);
	}, [setDraggableRef, setDroppableRef]);

	// Memoize event handlers
	const handleDblClick = useCallback(() => {
		if (editableSections.includes(section.type)) {
			handleEditing();
		}
	}, [section.type]);

	const handleEditing = useCallback(() => {
		// Store original content when entering edit mode
		originalContentRef.current = section.content;
		setHasUserEdited(false);

		if (section?.isSplitPair) {
			setIsOpenEditSectionModal(true);
		} else {
			setIsEditing(true);
			setIsSectionEditing(true);
		}
	}, [section?.isSplitPair, section.content, setIsSectionEditing]);

	const handleEditorReady = useCallback((editor: any) => {
		editorRef.current = editor;

		// Track cursor position changes in the editor
		if (editor?._tiptap) {
			editor._tiptap.on('selectionUpdate', () => {
				if (!isRestoringCursorRef.current && editor._tiptap) {
					const { from, to } = editor._tiptap.state.selection;
					cursorPositionRef.current = { from, to };
				}
			});

			// Track when user actually makes changes
			editor._tiptap.on('update', ({ transaction }) => {
				// Only mark as edited if the transaction actually changed the document
				if (transaction.docChanged) {
					setHasUserEdited(true);
				}
			});
		}
	}, []);

	const handleSave = useCallback((e: React.MouseEvent) => {
		e.stopPropagation();
		if (editorRef.current) {
			// Save cursor position before getting content
			if (editorRef.current._tiptap) {
				const { from, to } = editorRef.current._tiptap.state.selection;
				cursorPositionRef.current = { from, to };
			}

			// Get content from editor
			// Use getData() which returns sourceCodeRef.current from CustomEditor
			// This preserves the HTML structure better than getHTML()
			const content = editorRef.current.getData();

			// Update ref immediately for instant tracking
			contentRef.current = content;

			// Update local state for UI feedback
			setLocalContent(content);

			const styles = extractStyles(section.content, {
				includeTagStyles: true,
				includeInlineStyles: true,
				mergeNestedStyles: true,
				filterEmptyStyles: true
			});
			const single = getSingleStyles(styles);
			// Merge styles safely
			const mergedStyles = {
				...(section.attributes?.styles || {}),
				...single
			};

			// Merge attributes, giving priority to new props
			const mergedAttributes = {
				...(section.attributes || {}),
				styles: mergedStyles,
			};

			// Use debounced update to prevent excessive re-renders and API calls
			debouncedUpdate(content, { is_edited_from_source: isEditedFromSource, attributes: mergedAttributes });
		}
		setIsEditing(false);
		setIsSectionEditing(false)
		setIsOpenEditSectionModal(false);
		setHasUserEdited(false); // Reset edit flag
	}, [debouncedUpdate, isEditedFromSource, extractStyles, getSingleStyles, section.content, section.attributes, setIsSectionEditing, hasUserEdited]);

	const handleSplit = useCallback(() => {
		const content = section.content || '';
		const firstHalf = content;
		const secondHalf = '';
		onSplit(firstHalf, secondHalf);
	}, [section.content, onSplit]);

	const handleAddPlaceholder = useCallback((placeholder: string) => {
		if (editorRef.current) {
			const viewFragment = editorRef.current.data.processor.toView(
				`{{${placeholder}}}`
			);
			const modelFragment = editorRef.current.data.toModel(viewFragment);
			editorRef.current.model.insertContent(modelFragment);
		}
	}, []);

	const handleMouseEnter = useCallback(() => {
		if (mouseHoverTimeoutRef.current !== null) {
			window.clearTimeout(mouseHoverTimeoutRef.current);
		}
		mouseHoverTimeoutRef.current = window.setTimeout(() => {
			setIsHovered(true);
		}, 50);
	}, []);

	const handleMouseLeave = useCallback(() => {
		if (mouseHoverTimeoutRef.current !== null) {
			window.clearTimeout(mouseHoverTimeoutRef.current);
		}
		mouseHoverTimeoutRef.current = window.setTimeout(() => {
			setIsHovered(false);
		}, 50);
	}, []);

	const handleClick = useCallback((e: React.MouseEvent) => {
		e.stopPropagation();
		onSelect();
	}, [onSelect]);

	const handleDeleteClick = useCallback((e: React.MouseEvent) => {
		e.stopPropagation();
		onDelete();
	}, [onDelete]);

	const handleCancelEdit = useCallback(() => {
		setIsEditing(false);
		setIsSectionEditing(false)
	}, [setIsSectionEditing]);

	const handleCheckboxChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setIsEditedFromSource(e.target.checked);
	}, []);

	const handleSelectChange = useCallback((e: any) => {
		if (e.target.value) {
			handleAddPlaceholder(e.target.value as string);
			e.target.value = '';
		}
	}, [handleAddPlaceholder]);

	// Cleanup function for hover timeout
	React.useEffect(() => {
		return () => {
			if (mouseHoverTimeoutRef.current !== null) {
				window.clearTimeout(mouseHoverTimeoutRef.current);
			}
		};
	}, []);

	// Sync local content with section content when it changes externally
	// and restore cursor position if editor is focused
	React.useEffect(() => {
		// Only update if content actually changed from external source
		// Skip if we're currently editing (content changes come from user input)
		if (contentRef.current === section.content) {
			return;
		}

		// Update both ref and state for consistency
		contentRef.current = section.content;
		setLocalContent(section.content);

		// Only restore cursor position if:
		// 1. Editor is active (isEditing)
		// 2. Content change came from external source (not from user typing)
		// 3. We're not currently in the middle of restoring cursor
		if (isEditing && editorRef.current?._tiptap && cursorPositionRef.current && !isRestoringCursorRef.current) {
			const editor = editorRef.current._tiptap;
			const { from, to } = cursorPositionRef.current;

			// Use requestAnimationFrame to ensure DOM has updated
			requestAnimationFrame(() => {
				try {
					isRestoringCursorRef.current = true;

					// Validate that the cursor position is still valid after content update
					const docSize = editor.state.doc.content.size;
					const validFrom = Math.min(from, docSize);
					const validTo = Math.min(to, docSize);

					// Restore selection
					editor.commands.setTextSelection({ from: validFrom, to: validTo });

					// Ensure editor maintains focus
					if (!editor.isFocused) {
						editor.commands.focus();
					}
				} catch (error) {
					console.warn('Failed to restore cursor position:', error);
				} finally {
					isRestoringCursorRef.current = false;
				}
			});
		}
	}, [section.content, isEditing]);

	// Memoize content rendering to prevent unnecessary re-renders
	// Only re-render when isEditing state changes or when not editing and localContent changes
	const contentRenderer = useMemo(() => {
		if (isEditing) {
			return (
				<Suspense fallback={<Skeleton />}>
					<CustomEditor
						key={`editor-${section.id}`}
						editorRef={editorRef}
						handleEditorReady={handleEditorReady}
						data={localContent}
						onError={(error: Error) => console.error('Editor error:', error)}
					/>
				</Suspense>
			);
		}

		return (
			<Box
				dangerouslySetInnerHTML={{ __html: localContent }}
				sx={{
					position: 'relative',
					py: section.type === 'divider' ? 1 : 0,
					lineHeight: 1.6,
					flex: 1,
					minHeight: !localContent || localContent === '<p style="font-size: 16px; color: #333333; line-height: 1.5;">New section</p>' ? '60px' : 'auto',
					'& p': { margin: 0 }
				}}
			/>
		);
	}, [isEditing, localContent, section.type, section.id]);

	// Memoize placeholder options
	const placeholderOptions = useMemo(() =>
		placeholders.map((option) => (
			<MenuItem key={option.value} value={option.value}>
				<Typography type='s4' color='theme.secondary.1000'>{option.label}</Typography>
			</MenuItem>
		))
		, [placeholders]);

	return (
		<Paper
			ref={mergedRef}
			style={style}
			elevation={isHovered || isSelected ? 3 : 1}
			sx={paperSx}
			onClick={handleClick}
			onDoubleClick={handleDblClick}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}>
			{!isPreview && isOver && !isDragging && (
				<Box
					sx={{
						position: 'absolute',
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						border: '2px dashed',
						borderColor: 'primary.main',
						borderRadius: 1,
						bgcolor: 'rgba(25, 118, 210, 0.04)',
						zIndex: 10,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						pointerEvents: 'none'
					}}
				>
					<Typography type='s2' color='primary' weight='medium'>Drop here</Typography>
				</Box>
			)}
			{!isPreview && (
				<Box
					sx={{
						bgcolor: 'grey.50',
						p: 1,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						borderBottom: '1px solid',
						borderColor: 'grey.200'
					}}>
					<Box
						{...listeners}
						{...attributes}
						sx={{
							display: 'flex',
							alignItems: 'center',
							cursor: 'grab',
							px: 1,
							py: 0.5,
							borderRadius: 1,
							transition: 'background-color 0.2s ease',
							'&:hover': { bgcolor: 'grey.100' },
							'&:active': { cursor: 'grabbing' }
						}}>
						<DragIndicatorIcon
							sx={{ color: 'grey.600', fontSize: '1.2rem', mr: 0.5 }}
						/>
						<Typography type='s3' color='theme.secondary.600' weight='medium'>Move</Typography>
					</Box>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
						{isEditing ? (
							<>
								<FormControlLabel control={
									<Checkbox
										onChange={handleCheckboxChange}
										defaultChecked={isEditedFromSource} />}
									label={<Typography type='s2' color='theme.secondary.1000'>Edited from source</Typography>} />
								<FormControl size='small' sx={{ minWidth: 160 }}>
									<Select
										displayEmpty
										value=''
										size='small'
										renderValue={() => 'Add placeholder...'}
										sx={{
											bgcolor: 'white',
											borderRadius: 1,
											'& .MuiSelect-select': { py: 0.75 }
										}}
										onChange={handleSelectChange}>
										{placeholderOptions}
									</Select>
								</FormControl>
								<Box>
									<IconButton size='small' onClick={handleSave}>
										<Save fontSize='small' color='inherit' />
									</IconButton>
									<IconButton size='small' onClick={handleCancelEdit} color='error'>
										<Close fontSize='small' color='inherit' />
									</IconButton>
								</Box>
							</>
						) : (
							<Fade in={isHovered || isSelected} timeout={200}>
								<Box sx={{ display: 'flex', gap: 0.5 }}>
									{section?.is_edited_from_source && (
										<Chip
											label="Edited From Source"
											size="small"
											sx={(theme) => ({
												textTransform: 'capitalize',
												backgroundColor: theme.palette.theme?.tertiary1?.[200],
												color: theme.palette.theme?.tertiary1?.[900]
											})}
										/>
									)}
									{editableSections.includes(section.type) &&
										<IconButton
											size='small'
											onClick={handleEditing}
											sx={{
												bgcolor: 'grey.100',
												'&:hover': { bgcolor: 'grey.200' }
											}}>
											<Edit fontSize='small' sx={{ color: 'theme.secondary.1000' }} />
										</IconButton>
									}
									<IconButton
										size='small'
										onClick={handleSplit}
										sx={{
											bgcolor: 'grey.100',
											'&:hover': { bgcolor: 'grey.200' },
											opacity: section?.splitWidth === 25 ? 0.5 : 1
										}}
										disabled={section?.splitWidth === 25}
									>
										<SplitIcon fontSize='small' color='primary' />
									</IconButton>
									<IconButton
										size='small'
										onClick={handleDeleteClick}
										sx={{
											bgcolor: 'grey.100',
											'&:hover': { bgcolor: 'grey.200' }
										}}>
										<Trash fontSize='small' sx={{ color: 'theme.error.500' }} />
									</IconButton>
								</Box>
							</Fade>
						)}
					</Box>
				</Box>
			)}
			<Box sx={{ p: 3, bgcolor: 'white' }}>
				<style>
					{`
						a {
							color: #007bff !important;
							text-decoration: none !important;
						}
						a:hover {
							text-decoration: underline;
						}
           `}
				</style>
				{contentRenderer}
			</Box>
			{isOpenEditSectionModal && <EditSectionModal
				isOpen={isOpenEditSectionModal}
				setIsOpen={setIsOpenEditSectionModal}
				modalProps={{ open: isOpenEditSectionModal }}
				placeholders={placeholders}
				handleSave={handleSave}
				handleAddPlaceholder={handleAddPlaceholder}
				handleEditorReady={handleEditorReady}
				section={section}
			/>}
		</Paper>
	);
};

// Custom comparison function to prevent unnecessary re-renders
// Return true if props are equal (skip re-render), false if different (re-render)
const arePropsEqual = (prevProps: EditorSectionProps, nextProps: EditorSectionProps): boolean => {
	// Check if section data has changed
	if (prevProps.section.id !== nextProps.section.id) return false;
	if (prevProps.section.content !== nextProps.section.content) return false;
	if (prevProps.section.type !== nextProps.section.type) return false;
	if (prevProps.section.is_edited_from_source !== nextProps.section.is_edited_from_source) return false;
	if (prevProps.section.isSplitPair !== nextProps.section.isSplitPair) return false;
	if (prevProps.section.splitWidth !== nextProps.section.splitWidth) return false;

	// Check if interaction states have changed
	if (prevProps.isSelected !== nextProps.isSelected) return false;
	if (prevProps.isDragging !== nextProps.isDragging) return false;
	if (prevProps.isOver !== nextProps.isOver) return false;
	if (prevProps.isPreview !== nextProps.isPreview) return false;

	// Check if placeholders array reference has changed
	if (prevProps.placeholders !== nextProps.placeholders) return false;

	// Deep comparison for section attributes (styles, etc.)
	const prevAttrs = JSON.stringify(prevProps.section.attributes);
	const nextAttrs = JSON.stringify(nextProps.section.attributes);
	if (prevAttrs !== nextAttrs) return false;

	// All props are equal, skip re-render
	return true;
};

// Wrap component with React.memo and custom comparison function
const MemoizedEditorSection = React.memo(EditorSection, arePropsEqual);
MemoizedEditorSection.displayName = 'EditorSection';

export default MemoizedEditorSection;